import React, { useEffect, useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Container from '../../Components/Container'
import DataTable from 'react-data-table-component'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import toast, { Toaster } from 'react-hot-toast'

const readJson = async (res) => {
    if (!res.ok) throw new Error('Sunucu yanıt vermedi')
    return res.json()
}

const normalizeRows = (payload) => {
    if (Array.isArray(payload)) return payload
    if (payload && Array.isArray(payload.data)) return payload.data
    if (
        payload != null &&
        typeof payload === 'object' &&
        Number(payload.status) === 400
    ) {
        return []
    }
    return []
}

/** Sekme + tablo alanı için tutarlı renk dilimi (satış / iade / zayiat / hepsi) */
const DETAIL_THEMES = {
    sales: {
        label: 'Satış hareketleri',
        captionClass: 'text-emerald-800',
        tabSelected:
            'from-emerald-500 to-teal-600 ring-2 ring-emerald-300 shadow-emerald-500/20',
        tabIdle:
            'bg-emerald-50 text-emerald-900 hover:bg-emerald-100/90',
        panel:
            'border-emerald-200/90 bg-emerald-50/35 shadow-md shadow-emerald-100/40 ring-1 ring-emerald-100/80',
        topBar: 'bg-gradient-to-r from-emerald-500 to-teal-600',
        headBg: '#ecfdf5',
        headBorder: '#6ee7b7',
        headText: '#047857',
        spinner: 'border-emerald-600',
    },
    return: {
        label: 'İade hareketleri',
        captionClass: 'text-amber-900',
        tabSelected:
            'from-amber-500 to-orange-600 ring-2 ring-amber-300 shadow-amber-500/20',
        tabIdle:
            'bg-amber-50 text-amber-950 hover:bg-amber-100/90',
        panel:
            'border-amber-200/90 bg-amber-50/35 shadow-md shadow-amber-100/40 ring-1 ring-amber-100/80',
        topBar: 'bg-gradient-to-r from-amber-500 to-orange-600',
        headBg: '#fffbeb',
        headBorder: '#fcd34d',
        headText: '#b45309',
        spinner: 'border-amber-600',
    },
    casualty: {
        label: 'Zayiat hareketleri',
        captionClass: 'text-rose-900',
        tabSelected:
            'from-rose-500 to-red-600 ring-2 ring-rose-300 shadow-rose-500/20',
        tabIdle: 'bg-rose-50 text-rose-950 hover:bg-rose-100/90',
        panel:
            'border-rose-200/90 bg-rose-50/35 shadow-md shadow-rose-100/40 ring-1 ring-rose-100/80',
        topBar: 'bg-gradient-to-r from-rose-500 to-red-600',
        headBg: '#fff1f2',
        headBorder: '#fda4af',
        headText: '#be123c',
        spinner: 'border-rose-600',
    },
    all: {
        label: 'Tüm hareketler (satış, iade, zayiat)',
        captionClass: 'text-indigo-900',
        tabSelected:
            'from-indigo-500 to-violet-600 ring-2 ring-indigo-300 shadow-indigo-500/20',
        tabIdle:
            'bg-indigo-50 text-indigo-900 hover:bg-indigo-100/90',
        panel:
            'border-indigo-200/90 bg-indigo-50/30 shadow-md shadow-indigo-100/40 ring-1 ring-indigo-100/80',
        topBar: 'bg-gradient-to-r from-indigo-500 to-violet-600',
        headBg: '#eef2ff',
        headBorder: '#a5b4fc',
        headText: '#4338ca',
        spinner: 'border-indigo-600',
    },
}

const buildTableStyles = (theme) => ({
    table: {
        style: {
            backgroundColor: 'white',
            borderRadius: '0.5rem',
        },
    },
    headRow: {
        style: {
            backgroundColor: theme.headBg,
            borderBottomWidth: '1px',
            borderBottomColor: theme.headBorder,
        },
    },
    headCells: {
        style: {
            color: theme.headText,
            fontSize: '0.75rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '1rem',
        },
    },
    cells: {
        style: {
            fontSize: '0.875rem',
            padding: '1rem',
            color: '#334155',
        },
    },
})

const DetailsTab = ({ children, theme }) => (
    <Tab as='span' className='focus:outline-none'>
        {({ selected }) => (
            <button
                type='button'
                className={
                    'rounded-full px-5 py-2 text-sm font-semibold shadow-sm transition-all duration-200 sm:px-6 sm:text-base ' +
                    (selected
                        ? 'scale-[1.02] bg-gradient-to-r text-white shadow-lg ' +
                          theme.tabSelected
                        : theme.tabIdle + ' hover:shadow-md')
                }
            >
                {children}
            </button>
        )}
    </Tab>
)

const Details = () => {
    const [searchParams] = useSearchParams()
    const category = searchParams.get('c') ?? ''
    const store = searchParams.get('s') ?? ''

    const [loading, setLoading] = useState(true)
    const [sales, setSales] = useState([])
    const [returns, setReturns] = useState([])
    const [casualty, setCasualty] = useState([])
    const [allRows, setAllRows] = useState([])

    const hasParams = Boolean(category && store)

    useEffect(() => {
        if (!hasParams) {
            setSales([])
            setReturns([])
            setCasualty([])
            setAllRows([])
            setLoading(false)
            return
        }

        let cancelled = false
        setLoading(true)

        const fetchAction = async (action) => {
            const formdata = new FormData()
            formdata.append('action', action)
            formdata.append('store', store)
            formdata.append('category', category)
            const raw = await fetch(
                process.env.REACT_APP_BASE_URL + 'panel.php',
                { method: 'POST', body: formdata }
            ).then(readJson)
            return normalizeRows(raw)
        }

        ;(async () => {
            try {
                const [ret, sell, cas, merged] = await Promise.all([
                    fetchAction('selectReturn'),
                    fetchAction('selectSell'),
                    fetchAction('selectCasualty'),
                    fetchAction('details'),
                ])
                if (cancelled) return
                setReturns(ret)
                setSales(sell)
                setCasualty(cas)
                setAllRows(merged)
            } catch {
                if (!cancelled) {
                    toast.error('Veriler yüklenirken hata oluştu')
                    setReturns([])
                    setSales([])
                    setCasualty([])
                    setAllRows([])
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [category, store, hasParams])

    const columns = useMemo(
        () => [
            { name: 'No', selector: (row) => row.id, sortable: true },
            { name: 'İsim', selector: (row) => row.name, sortable: true },
            {
                name: 'Barkod',
                selector: (row) => row.barcode,
                sortable: true,
            },
            { name: 'Adet', selector: (row) => row.count, sortable: true },
            { name: 'Fiyat', selector: (row) => row.price, sortable: true },
            { name: 'Maliyet', selector: (row) => row.cost, sortable: true },
        ],
        []
    )

    const noData = (
        <div className='py-12 text-center text-sm text-slate-500'>
            Bu sekme için kayıt yok.
        </div>
    )

    const tableShell = (data, themeKey) => {
        const theme = DETAIL_THEMES[themeKey]
        return (
            <div
                className={`overflow-hidden rounded-xl border p-2 sm:p-3 ${theme.panel}`}
            >
                <div
                    className={`mb-3 h-1.5 w-full rounded-full ${theme.topBar}`}
                    aria-hidden
                />
                <p
                    className={`mb-3 px-1 text-xs font-bold uppercase tracking-wide sm:text-sm ${theme.captionClass}`}
                >
                    {theme.label}
                </p>
                {loading ? (
                    <div className='flex h-40 flex-col items-center justify-center gap-2 text-slate-600'>
                        <div
                            className={`h-8 w-8 animate-spin rounded-full border-2 border-t-transparent ${theme.spinner}`}
                        />
                        <span className='text-sm'>Yükleniyor…</span>
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        pagination
                        data={data}
                        customStyles={buildTableStyles(theme)}
                        noDataComponent={noData}
                    />
                )}
            </div>
        )
    }

    return (
        <Container padding bg='#f8fafc'>
            <Toaster
                position='top-right'
                toastOptions={{ className: 'text-sm font-medium shadow-lg' }}
            />

            <div className='mx-auto w-full max-w-[1400px] space-y-6 px-4 pb-16 pt-2 sm:px-6 lg:px-8'>
                {!hasParams ? (
                    <div className='rounded-2xl border border-amber-200/80 bg-amber-50/80 p-8 text-center shadow-sm'>
                        <p className='text-slate-800'>
                            Depo veya kategori seçilmedi. Lütfen paneleden bir
                            kategori satırına tıklayarak bu sayfaya gelin.
                        </p>
                        <Link
                            to='/dashboard/panel'
                            className='mt-4 inline-flex rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700'
                        >
                            Panele dön
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                            <div className='flex flex-col items-center justify-center rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm'>
                                <h2 className='mb-2 text-lg font-bold text-slate-800'>
                                    Depo
                                </h2>
                                <hr className='mb-3 h-px w-full border-0 bg-slate-200' />
                                <span className='text-2xl font-semibold text-indigo-600'>
                                    {store}
                                </span>
                            </div>
                            <div className='flex flex-col items-center justify-center rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm'>
                                <h2 className='mb-2 text-lg font-bold text-slate-800'>
                                    Kategori
                                </h2>
                                <hr className='mb-3 h-px w-full border-0 bg-slate-200' />
                                <span className='text-2xl font-semibold text-indigo-600'>
                                    {category}
                                </span>
                            </div>
                        </div>

                        <TabGroup className='flex w-full flex-col gap-6'>
                            <TabList className='flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                                <div className='flex flex-wrap items-center gap-3'>
                                    <DetailsTab theme={DETAIL_THEMES.sales}>
                                        Satış
                                    </DetailsTab>
                                    <DetailsTab theme={DETAIL_THEMES.return}>
                                        İade
                                    </DetailsTab>
                                    <DetailsTab theme={DETAIL_THEMES.casualty}>
                                        Zayiat
                                    </DetailsTab>
                                </div>
                                <div className='flex shrink-0'>
                                    <DetailsTab theme={DETAIL_THEMES.all}>
                                        Hepsi
                                    </DetailsTab>
                                </div>
                            </TabList>
                            <TabPanels className='w-full'>
                                <TabPanel>
                                    {tableShell(sales, 'sales')}
                                </TabPanel>
                                <TabPanel>
                                    {tableShell(returns, 'return')}
                                </TabPanel>
                                <TabPanel>
                                    {tableShell(casualty, 'casualty')}
                                </TabPanel>
                                <TabPanel>
                                    {tableShell(allRows, 'all')}
                                </TabPanel>
                            </TabPanels>
                        </TabGroup>
                    </>
                )}
            </div>
        </Container>
    )
}

export default Details
